import React, { forwardRef, useImperativeHandle, useRef } from 'react';
import { Turnstile } from 'react-turnstile';

const RAW_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;
const SITE_KEY =
  typeof RAW_SITE_KEY === 'string' && RAW_SITE_KEY.trim() && RAW_SITE_KEY !== 'undefined'
    ? RAW_SITE_KEY.trim()
    : '';

// One-time runtime warning so missing prod env var is obvious in browser console.
if (!SITE_KEY && typeof window !== 'undefined' && !window.__antispamWarned) {
  // eslint-disable-next-line no-console
  console.warn(
    '[AntiSpamFields] REACT_APP_TURNSTILE_SITE_KEY is missing or empty. ' +
      'Cloudflare Turnstile widget is disabled. Set it in your hosting provider env vars and rebuild.'
  );
  window.__antispamWarned = true;
}

/**
 * Anti-spam fields component.
 *
 * Renders:
 *  - a hidden honeypot input (name="website") — captured via ref,
 *  - the Cloudflare Turnstile widget (only when SITE_KEY is a non-empty string).
 *
 * Expose `getHoneypotValue()` via ref so the parent can include it in the payload.
 */
const AntiSpamFields = forwardRef(({ onTurnstileVerify, turnstileRef }, ref) => {
  const honeypotRef = useRef(null);

  useImperativeHandle(ref, () => ({
    getHoneypotValue: () => honeypotRef.current?.value || '',
  }));

  return (
    <>
      {/* Hidden honeypot — bots often auto-fill name="website" */}
      <input
        ref={honeypotRef}
        type="text"
        name="website"
        tabIndex={-1}
        autoComplete="off"
        style={{ position: 'absolute', left: '-9999px', opacity: 0, height: 0, width: 0 }}
        aria-hidden="true"
        data-testid="honeypot-field"
        defaultValue=""
      />

      {/* Cloudflare Turnstile — only when site key is properly configured */}
      {SITE_KEY ? (
        <div className="mt-3" data-testid="turnstile-widget">
          <Turnstile
            userRef={turnstileRef}
            sitekey={SITE_KEY}
            onVerify={onTurnstileVerify}
            onError={() => onTurnstileVerify?.('')}
            onExpire={() => onTurnstileVerify?.('')}
            theme="light"
            size="flexible"
          />
        </div>
      ) : null}
    </>
  );
});

AntiSpamFields.displayName = 'AntiSpamFields';

export default AntiSpamFields;
